"use client";

import { useGlobalContext } from "@/app/context";
import { Mic, TvIcon } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import React, { useEffect, useRef } from "react";

const CommentateToggleBar: React.FC = () => {
    const router = useRouter();
    const pathname = usePathname();
    const context = useGlobalContext();
    const manualToggleRef = useRef(false);

    // Add error handling for context
    if (!context) {
        console.error("useGlobalContext returned undefined");
        return null;
    }

    const { userType, setUserType } = context;

    // Determine if user is on a commentator page based on current route
    const isOnCommentatorPage = pathname.startsWith('/commentate');

    // Sync userType with current page on route changes (but not immediately after manual toggles)
    useEffect(() => {
        if (!setUserType || manualToggleRef.current) {
            // Reset the manual toggle flag after a short delay
            if (manualToggleRef.current) {
                setTimeout(() => {
                    manualToggleRef.current = false;
                }, 1000);
            }
            return;
        }

        const shouldBeCommentator = isOnCommentatorPage;

        if (shouldBeCommentator && userType !== "commentator") {
            setUserType("commentator");
        } else if (!shouldBeCommentator && userType !== "viewer") {
            setUserType("viewer");
        }
    }, [pathname, userType, setUserType, isOnCommentatorPage]);

    const handleToggle = () => {
        if (!setUserType) {
            console.error("setUserType is undefined");
            return;
        }

        // Mark that this is a manual toggle to prevent automatic sync
        manualToggleRef.current = true;

        if (userType === "viewer") {
            setUserType("commentator");
            router.push("/commentate");
        } else {
            setUserType("viewer");
            router.push("/home");
        }
    };

    // Use route-based determination for visual state (more reliable than userType state)
    const isCommentator = isOnCommentatorPage;

    return (
        <div className="w-full bg-widertalk-lime border-b border-widertalk-lime py-4">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center">
                    {/* Slider Container */}
                    <div className="flex items-center gap-6">
                        {/* Left Label - View Mode */}
                        <div className={`flex items-center gap-2 transition-colors duration-300 ${!isCommentator ? 'text-widertalk-navy font-semibold' : 'text-widertalk-navy/60'
                            }`}>
                            <TvIcon size={18} />
                            <span className="text-sm">View Mode</span>
                        </div>

                        {/* Slider Track */}
                        <div
                            className="w-32 sm:w-40 lg:w-48 h-8 bg-widertalk-navy rounded-full relative cursor-pointer shadow-inner border-widertalk-navy/30"
                            onClick={handleToggle}
                        >
                            {/* Slider Handle */}
                            <div
                                className={`absolute top-1 w-6 h-6 bg-widertalk-lime rounded-full shadow-lg transition-transform duration-300 ease-in-out transform border border-widertalk-lime/30`
                                    + (isCommentator ? ' left-[calc(100%-1.75rem)]' : ' left-1')}
                                style={{
                                    left: isCommentator ? 'calc(100% - 1.75rem)' : '0.25rem',
                                    right: isCommentator ? undefined : undefined
                                }}
                            />
                        </div>

                        {/* Right Label - Commentate Mode */}
                        <div className={`flex items-center gap-2 transition-colors duration-300 ${isCommentator ? 'text-widertalk-navy font-semibold' : 'text-widertalk-navy/60'
                            }`}>
                            <Mic size={18} />
                            <span className="text-sm">Commentate Mode</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommentateToggleBar;
